import * as React from 'react'
import cx from 'classnames'

import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import Button from 'components/Button'

import styles from './ConsultationPendingBookingModal.sass'
import { format } from 'date-fns'
import CompletionToggle from 'components/CompletionToggle'
import { observer } from 'mobx-react'
import Avatar from 'components/Avatar'
import { TimeRange } from 'jobs/TimeRange'
import ConsultationPendingBookingModalViewModel from './ConsultationPendingBookingModal'

interface Props {
  vm: ConsultationPendingBookingModalViewModel
}

export default observer(function ConsultationPendingBookingModal({
  vm,
}: Props) {
  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader withCloseButton></ModalSheetHeader>
      <ModalSheetContent leftAligned>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            <span className={styles.title}>Select new appointment</span>
          </ModalSheetContentTitle>

          <ModalSheetContentDescription>
            <span className={styles.description}>
              {`This job requires a ${vm.requestedLength} minute consultation call. To confirm a
              new appointment, select a new date and time to continue.`}
              {vm.consultationIsForTaxJob && (
                <>
                  <br />
                  <br />
                  {` If you're not available at any of these times, reach out to the client
                    via the chat and ask them to update their availability.`}
                </>
              )}
            </span>
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <div className={styles.scheduledDays}>
          {vm.availableDays.map((d) => (
            <div key={d.formattedDay.valueOf()}>
              <div className={styles.dayTitle}>{d.formattedDay}</div>
              {d?.timeSlots?.map((t, i) => (
                <TimeSlotListItem key={i} index={i} vm={vm} range={t} />
              ))}
            </div>
          ))}
        </div>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          {!vm.consultationIsForTaxJob && (
            <Button
              type="default"
              loading={vm.forfeit.pending}
              onClick={vm.forfeit}
            >
              Forfeit Job
            </Button>
          )}
          <Button
            submit
            type="primary"
            loading={vm.book.pending}
            onClick={vm.book}
            data-test="consultation-booking-confirm-button"
          >
            Confirm Appointment
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface TimeSlotListItemProps {
  index: number
  range: TimeRange
  vm: ConsultationPendingBookingModalViewModel
}

const TimeSlotListItem = observer(function TimeSlotListItem({
  index,
  range,
  vm,
}: TimeSlotListItemProps) {
  const bookedConsultation = vm.bookedConsultations.find((c) =>
    c.bookedTime?.isWithin(range),
  )

  const isBooked = Boolean(bookedConsultation)
  const selected =
    vm.selectedTimeSlot?.isEqual(range) || Boolean(bookedConsultation)

  return (
    <div
      className={cx(
        styles.timeSlotItem,
        selected && styles.selected,
        isBooked && styles.disabled,
      )}
      data-test={`time-slot-item-${range.startTime.getDay()}-${index}`}
      onClick={() => !isBooked && vm.selectTimeSlot(range)}
    >
      <div className={styles.completionToggle}>
        <CompletionToggle
          checkType="tick"
          size={20}
          completed={selected}
          disabled={isBooked}
        />
      </div>
      <div className={styles.timeSlotText}>
        {selected && (
          <div className={styles.jobName}>
            {isBooked ? 'Unavailable' : 'Selected'}
          </div>
        )}
        {format(range.startTime, 'h:mm')} - {format(range.endTime, 'h:mmaaa')}
      </div>
      {selected && (
        <>
          <Avatar
            subject={vm.clientMember.user}
            size={24}
            greyscale={isBooked}
          />
          <div className={styles.avatars}>
            <Avatar
              subject={vm.currentMember.user}
              size={24}
              greyscale={isBooked}
            />
          </div>
        </>
      )}
    </div>
  )
})
